// Defense-in-depth startup check. The authoritative guarantee that a session is
// bound to the right task lives server-side (requireTaskAuth + the Postgres
// partial-unique index on active CodespaceSessions). This check exists so that
// if those invariants ever regress, a human sees a loud WARN immediately
// instead of the symptoms SEC-9 produced (multiple agents silently clobbering
// the same branch).
export interface SessionIdentityLogger {
  warn(message: string, data?: Record<string, unknown>): void;
  info?(message: string, data?: Record<string, unknown>): void;
}

export interface CheckSessionTaskIdentityParams {
  sessionId: string | undefined;
  taskId: string;
  fetchSessionTaskId: (sessionId: string) => Promise<string>;
  logger: SessionIdentityLogger;
}

export async function checkSessionTaskIdentity(
  params: CheckSessionTaskIdentityParams,
): Promise<"match" | "mismatch" | "skipped" | "error"> {
  const { sessionId, taskId, fetchSessionTaskId, logger } = params;
  if (!sessionId || sessionId === taskId) return "skipped";
  let sessionTaskId: string;
  try {
    sessionTaskId = await fetchSessionTaskId(sessionId);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logger.warn("Could not verify session/task identity — continuing (defense-in-depth only)", {
      sessionId,
      taskId,
      error: message,
    });
    return "error";
  }
  if (sessionTaskId !== taskId) {
    logger.warn(
      "!!! CONVEYOR_SESSION_ID is bound to a different task than CONVEYOR_TASK_ID — server-side guards should still block mutations, but this indicates a misconfiguration or replayed token.",
      { sessionId, envTaskId: taskId, sessionTaskId },
    );
    return "mismatch";
  }
  return "match";
}
